<script setup lang="ts">
import { computed } from 'vue';

import { globalConfig } from '@/store/globalConfig';
import { useGlobalStyle, useTemplate } from '@/store/templateData';


defineOptions({
  name: 'ModuleStreamRoomDisplay'
});

interface Props {
  module?: {
    components?: {
      title?: {
        text?: string;
      };
      subtitle?: {
        text?: string;
      };
      background?: {
        src?: string;
      };
    };
  };
}

const props = withDefaults(defineProps<Props>(), {
  module: () => ({
    components: {
      title: {
        text: ''
      },
      subtitle: {
        text: ''
      },
      background: {
        src: ''
      }
    }
  })
});

const components = computed(() => props.module.components);

const { isGeneralCmsStreamCanPlay } = useTemplate();

const streamRoomBackgroundStyle = computed(() => {
  const src = components.value?.background?.src;
  return !src ? '' : `background-image: url('${src}');`;
});

const needRender = computed(() => [
  isGeneralCmsStreamCanPlay.value,
].some(Boolean));

const localStreamId = globalConfig.value.localStreamId;
const { titleStyle, subtitleStyle } = useGlobalStyle();
</script>

<template>
<div class="module-stream-room-display" v-if="needRender">
  <div class="module-stream-room-display__header">
    <div
      class="module-stream-room-display__title"
      v-if="components?.title?.text"
      :style="titleStyle"
    >
      {{ components.title.text }}
    </div>

    <div
      class="module-stream-room-display__subtitle"
      v-if="components?.subtitle?.text"
      :style="subtitleStyle"
    >
      {{ components.subtitle.text }}
    </div>
  </div>

  <div class="module-stream-room-display__wrapper">
    <div class="module-stream-room-display__box" :style="streamRoomBackgroundStyle">
      <div class="module-stream-room-display__stream" :id="localStreamId"></div>
    </div>
  </div>
</div>
</template>
<style lang="scss" scoped>
$width: 375px;
$height: 666px;
$desktop-fixed-width: 1178px;
$stream-room-bg: #F0F0F0;

.module-stream-room-display {
  @apply px-0 py-4;

  &__header {
    @apply mx-5;
  }

  &__title,
  &__subtitle {
    @apply mx-auto font-medium break-all;
    // mobile版直播間滿版，但文字要與其他 module 等寬 (留 20px 的 padding)
    @apply px-5;

    word-wrap: break-word;
  }

  &__title {
    @apply text-4xl text-center whitespace-pre-line;
  }

  &__subtitle {
    @apply text-xl text-left whitespace-pre-line mt-4;
  }

  &__wrapper {
    @apply relative w-full mx-auto mt-6;

    background-color: $stream-room-bg;

    @include aspect-ratio($height: 16, $width: 9);
  }

  &__box {
    @apply absolute inset-0 h-full w-full bg-center bg-cover;
  }

  &__stream {
    @apply mx-auto w-full h-full bg-black flex items-center justify-center;
  }

  &__play-icon {
    @apply w-12 h-12;
  }
}

.tablet .module-stream-room-display {
  &__header {
    @apply mx-6;
  }

  &__wrapper {
    @apply pb-0;

    height: $height;
  }

  &__stream {
    width: $width;
  }

  &__play-icon {
    @apply w-16 h-16;
  }

  &__title,
  &__subtitle {
    @apply px-10;
  }
}

.laptop .module-stream-room-display {
  @apply px-6 py-8;
  @apply w-full;

  max-width: calc(#{$desktop-fixed-width} + 2 * 24px); // include px-6;

  &__header {
    @apply mx-6;
  }

  &__wrapper {
    @apply pb-0;

    height: $height;
  }

  &__stream {
    width: $width;
  }

  &__play-icon {
    @apply w-16 h-16;
  }

  &__title,
  &__subtitle {
    @apply px-0;
  }
}

.desktop .module-stream-room-display {
  @apply px-6 py-8;

  width: $desktop-fixed-width;

  &__header {
    @apply mx-0;
  }

  &__wrapper {
    @apply mt-8 pb-0;

    height: $height;
  }

  &__stream {
    width: $width;
  }

  &__play-icon {
    @apply w-16 h-16;
  }

  &__title,
  &__subtitle {
    @apply px-0;
  }
}
</style>
